// This is the Deployment page
// It allows admin users to download the agent, copy their enrollment token,
// and get pre-built deployment commands for Intune, Tanium, and SCCM

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar"

const API_URL = config.API_URL;

export default function Deployment() {
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;
  const navigate = useNavigate();

  const [orgToken, setOrgToken] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);
  const [installerUrl, setInstallerUrl] = useState(null);
  const [serviceUrl, setServiceUrl] = useState(null);
  const [trayUrl, setTrayUrl] = useState(null);
  const [agentVersion, setAgentVersion] = useState(null);

  useEffect(() => {
    // Fetch org token
    axios
      .get(`${API_URL}/organizations/${ORG_ID}/token`)
      .then((res) => {
        setOrgToken(res.data.enrollment_token);
        setOrgName(res.data.org_name);
      })
      .catch(() => {});

    // Fetch current agent version and versioned download URLs
    axios
      .get(`${API_URL}/agent/version`)
      .then((res) => {
        setInstallerUrl(res.data.installer_url);
        setServiceUrl(res.data.service_url);
        setTrayUrl(res.data.tray_url);
        setAgentVersion(res.data.version);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function copyToClipboard(text, key) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  }

  const installCommand = `DriverWatchInstaller-${agentVersion}.exe --org-token ${orgToken} --silent`;
  const serviceInstallCommand = `DriverWatchService.exe install`;
  const serviceStartCommand = `DriverWatchService.exe start`;
  const trayInstallCommand = `DriverWatchTray.exe`;
  const intuneCommand = `DriverWatchService.exe --org-token ${orgToken}`;
  const sccmCommand = `DriverWatchService.exe --org-token ${orgToken}`;
  const taniumCommand = `Start-Process -FilePath "DriverWatchService.exe" -ArgumentList "--org-token ${orgToken}" -Verb RunAs`;

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Loading deployment info...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      <Navbar orgName={orgName} />

      {/* Main content */}
      <main className="px-8 py-8 max-w-4xl">

        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Agent Deployment</h1>
          <p className="text-gray-400 mt-1">Download the agent and deploy it to your devices</p>
        </div>

        {/* Step 1 - Download */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</div>
            <h2 className="text-white font-semibold text-lg">Download the Agent</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Download the universal Windows agent installer. The same .exe works for all organizations — the enrollment token identifies which org the device belongs to.
          </p>
          <button
            onClick={() => window.location.href = installerUrl}
            disabled={!installerUrl}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            Download DriverWatchInstaller.exe {agentVersion && `v${agentVersion}`}
          </button>
          <p className="text-gray-500 text-xs mt-3">
            The installer bundles both the Windows Service and Tray App. Run as Administrator to install everything automatically.
          </p>
          <div className="mt-3 flex gap-3">
            <a href={serviceUrl} className="text-gray-500 hover:text-gray-300 text-xs underline">DriverWatchService.exe</a>
            <a href={trayUrl} className="text-gray-500 hover:text-gray-300 text-xs underline">DriverWatchTray.exe</a>
          </div>
        </div>

        {/* Step 2 - Enrollment Token */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold">2</div>
            <h2 className="text-white font-semibold text-lg">Your Enrollment Token</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            This token identifies your organization. It is embedded into the install command and tells the agent where to send its reports.
          </p>
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-green-400 font-mono text-sm">
              {orgToken}
            </code>
            <button
              onClick={() => copyToClipboard(orgToken, "token")}
              className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              {copiedKey === "token" ? "✅ Copied!" : "Copy Token"}
            </button>
          </div>
        </div>

        {/* Step 3 - Run Installer */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold">3</div>
            <h2 className="text-white font-semibold text-lg">Run the Installer</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Run this command as Administrator. The installer will automatically install the Windows Service, add the tray app to startup, register in Add/Remove Programs, and start monitoring.
          </p>
          <div className="flex items-center gap-3 mb-4">
            <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-yellow-300 font-mono text-sm">
              {installCommand}
            </code>
            <button
              onClick={() => copyToClipboard(installCommand, "install")}
              className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              {copiedKey === "install" ? "✅ Copied!" : "Copy Command"}
            </button>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3">
            <p className="text-gray-400 text-xs">
              💡 The installer handles everything automatically — Windows Service (runs as SYSTEM), tray app (shows in system tray), Add/Remove Programs registration, and uninstaller. To uninstall go to Windows Settings → Apps → DriverWatch Agent.
            </p>
          </div>
        </div>

        {/* Step 4 - Deployment Tools */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold">4</div>
            <h2 className="text-white font-semibold text-lg">Deploy via RMM / MDM</h2>
          </div>
          <p className="text-gray-400 text-sm mb-6">
            Use these pre-built commands to deploy the agent at scale through your deployment platform.
          </p>

          {/* Intune */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium text-sm">Microsoft Intune</span>
              <span className="text-gray-500 text-xs">Win32 App — Install command</span>
            </div>
            <div className="flex items-center gap-3">
              <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-yellow-300 font-mono text-sm">
                {intuneCommand}
              </code>
              <button
                onClick={() => copyToClipboard(intuneCommand, "intune")}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-3 rounded-lg transition-colors whitespace-nowrap"
              >
                {copiedKey === "intune" ? "✅ Copied!" : "Copy"}
              </button>
            </div>
          </div>

          {/* SCCM */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium text-sm">SCCM / MECM</span>
              <span className="text-gray-500 text-xs">Program — Command line</span>
            </div>
            <div className="flex items-center gap-3">
              <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-yellow-300 font-mono text-sm">
                {sccmCommand}
              </code>
              <button
                onClick={() => copyToClipboard(sccmCommand, "sccm")}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-3 rounded-lg transition-colors whitespace-nowrap"
              >
                {copiedKey === "sccm" ? "✅ Copied!" : "Copy"}
              </button>
            </div>
          </div>

          {/* Tanium */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium text-sm">Tanium</span>
              <span className="text-gray-500 text-xs">PowerShell deployment</span>
            </div>
            <div className="flex items-center gap-3">
              <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-yellow-300 font-mono text-sm break-all">
                {taniumCommand}
              </code>
              <button
                onClick={() => copyToClipboard(taniumCommand, "tanium")}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-3 rounded-lg transition-colors whitespace-nowrap"
              >
                {copiedKey === "tanium" ? "✅ Copied!" : "Copy"}
              </button>
            </div>
          </div>

        </div>
            {/* Third-party tool disclaimer */}
        <p className="text-gray-500 text-xs mt-6">
          HP Image Assistant, Dell Command Update, and Lenovo System Updater are third-party tools owned and maintained by their respective manufacturers. AleroSys is not affiliated with HP, Dell, or Lenovo. These tools are made available for customer convenience only — AleroSys is not responsible for the content, updates, or behavior of manufacturer-released tools.
        </p>
      </main>
    </div>
  );
}